import { useState, useEffect } from "react";
import { ModalHeader } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import Select from "react-select";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import {
  postSlider,
  getSliderById,
  updateSlider,
  getCategoriesForSlider,
  getStoresForSlider,
  getProductsForSlider,
} from "../../MyApi/slider";
import ButtonGlobal from "../../Components/UI/buttonGlobal";
import i18next from "i18next";
import LangSelectModal from "../../Components/UI/langselectmodal";
import SkeletonWrapper from "../../Components/UI/skeletonWrapper";

const AddModalSlider = ({
  isOpen,
  onClose,
  refetch,
  mode = "create",
  sliderId = null,
}) => {
  const isEdit = mode === "edit";
  const isShow = mode === "show";
  const lang = i18next.language;
  const [selectedLangCode, setSelectedLangCode] = useState(lang);

  const [formData, setFormData] = useState({
    title: "",
    body: "",
    button_text: "",
    target: "",
    target_id: "",
    is_active: 1,
    sort_order: "",
    image: null,
  });

  const { data: sliderData, isLoading: sliderLoading } = useQuery(
    ["slider", sliderId, selectedLangCode],
    () => getSliderById(sliderId, selectedLangCode),
    {
      enabled: (isEdit || isShow) && !!sliderId,
    },
  );

  const { data: categoriesData } = useQuery(
    ["getCategoriesForSlider"],
    getCategoriesForSlider,
    {
      enabled: formData.target === "category",
    },
  );

  const { data: storesData } = useQuery(
    ["getStoresForSlider"],
    getStoresForSlider,
    {
      enabled: formData.target === "store",
    },
  );

  const { data: productsData } = useQuery(
    ["getProductsForSlider"],
    getProductsForSlider,
    {
      enabled: formData.target === "product",
    },
  );

  const mutation = useMutation({
    mutationKey: ["postSlider"],
    mutationFn: (postData) => postSlider(postData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      onClose();
      refetch();
    },
    onError: (error) => {
      toast.error(`${error.response.data.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateSlider"],
    mutationFn: (postData) => updateSlider(sliderId, postData),
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      onClose();
      refetch();
    },
    onError: (error) => {
      toast.error(`${error.response.data.message}`);
    },
  });

  useEffect(() => {
    if ((isEdit || isShow) && sliderData?.data?.slider) {
      const slider = sliderData.data.slider;
      setFormData({
        title: slider.title || "",
        body: slider.body || "",
        button_text: slider.button_text || "",
        target: slider.target || "",
        target_id: slider.target_id || "",
        is_active: Number(slider.is_active),
        sort_order: slider.sort_order || "",
        image:slider.image
      });
    } else if (mode === "create") {
      // Reset form
      setFormData({
        title: "",
        body: "",
        button_text: "",
        target: "",
        target_id: "",
        is_active: 1,
        sort_order: "",
      });
    }
  }, [isEdit, isShow, sliderData, isOpen, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTargetChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      target: selectedOption?.value || "",
      target_id: "", // Reset target_id when target changes
    }));
  };

  const handleTargetIdChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      target_id: selectedOption?.value || "",
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleChangeLanguage = (e) => {
    setSelectedLangCode(e.target.value);
  };

  const handleSubmit = async () => {
    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("body", formData.body);
    submitData.append("button_text", formData.button_text);
    submitData.append("target", formData.target);
    submitData.append("target_id", formData.target_id);
    submitData.append("is_active", formData.is_active);
    submitData.append("sort_order", formData.sort_order);

    // Add image if provided
    if (formData.image && formData.image instanceof File) {
      submitData.append("image", formData.image);
    }

    if (isEdit) {
      submitData.append("lang", selectedLangCode);
      submitData.append("_method", "PUT");
      updateMutation.mutate(submitData);
    } else {
      mutation.mutate(submitData);
    }
  };

  const modalTitle = isShow
    ? "عرض الشرائح"
    : isEdit
      ? "تعديل الشرائح"
      : "إضافة شرائح جديد";
  const buttonText = isEdit ? "تحديث" : "حفظ";

  const targetOptions = [
    { value: "category", label: "فئة" },
    { value: "store", label: "متجر" },
    { value: "product", label: "منتج" },
  ];

  const getTargetOptions = () => {
    switch (formData.target) {
      case "category":
        return categoriesData?.data?.categories?.map(cat => ({
          value: cat.id,
          label: cat.name
        })) || [];
      case "store":
        return storesData?.data?.Stores?.map(store => ({
          value: store.id,
          label: store.name
        })) || [];
      case "product":
        return productsData?.data?.products?.map(product => ({
          value: product.id,
          label: product.name
        })) || [];
      default:
        return [];
    }
  };

  const isLoadingTargetOptions = 
    (formData.target === "category" && categoriesData?.isLoading) ||
    (formData.target === "store" && storesData?.isLoading) ||
    (formData.target === "product" && productsData?.isLoading);

  return (
    <Modal isOpen={isOpen} centered backdrop={true} size="lg" className="add" toggle={onClose}>
      <ModalHeader  className="fs-5">
        {modalTitle}
      </ModalHeader>

      <ModalBody>
        {isEdit && (
          <LangSelectModal
            handleChange={handleChangeLanguage}
            selectedLangCode={selectedLangCode}
          />
        )}
        <div className="row add">
          <div className="col-md-6">
            <label className="form-label">العنوان</label>
            <input
              type="text"
              name="title"
              placeholder="أدخل عنوان السلايدر"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">نص الزر</label>
            <input
              type="text"
              name="button_text"
              placeholder="أدخل نص الزر"
              className="form-control"
              value={formData.button_text}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الوصف</label>
            <input
              type="text"
              name="body"
              placeholder="أدخل وصف السلايدر"
              className="form-control"
              value={formData.body}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">ترتيب العرض</label>
            <input
              type="number"
              name="sort_order"
              min={0}
              className="form-control"
              placeholder="ترتيب العرض"
              value={formData.sort_order}
              onChange={handleChange}
              disabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">الهدف</label>
            <Select
              placeholder="اختر نوع الهدف"
              options={targetOptions}
              value={targetOptions.find(opt => opt.value === formData.target)}
              onChange={handleTargetChange}
              isClearable
              isDisabled={isShow}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label className="form-label">رقم الهدف</label>
            <Select
              placeholder="اختر الهدف"
              options={getTargetOptions()}
              value={getTargetOptions().find(opt => opt.value === formData.target_id)}
              onChange={handleTargetIdChange}
              isClearable
              isDisabled={isShow || !formData.target}
              isLoading={isLoadingTargetOptions}
            />
          </div>

          <div className="col-12 mt-2">
            <label className="form-label">الصورة</label>
            <input
            
              type="file"
              name="image"
              accept="image/*"
              className="form-control"
              onChange={handleImageChange}
              disabled={isShow}
            />
            {formData.image && <img  src={formData.image} alt="صورة الشريحة" width={300} height={300}/>}
          </div>

          <div className="col-12 mt-2">
            <div>
              <label htmlFor="">نشط</label>
              <label class="switch">
                <input
                  type="checkbox"
                  name="is_active"
                  onChange={handleChange}
                  checked={formData.is_active === 1}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </ModalBody>

      {isShow ? null : (
        <ModalFooter>
          <div className="col-3">
            <ButtonGlobal
              className="btn-primary"
              onClick={handleSubmit}
              label={buttonText}
              isLoading={isEdit ? updateMutation.isLoading : mutation.isLoading}
              disabled={isEdit ? updateMutation.isLoading : mutation.isLoading}
            />
          </div>
          <button className="btn btn-white" type="button" onClick={onClose}>
            إلغاء
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default AddModalSlider;